import { useCallback, useEffect, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  clearStoredAccounts,
  getActiveUserId,
  listStoredAccounts,
  removeStoredAccount,
  type StoredAccount,
} from '@/lib/accountsStore';

// ── Account-switching orchestration (#297) ───────────────────────────────────
//
// Multi-account switching over the single supabase client. Token mutation funnels
// through `supabase.auth.setSession` / `signOut` so AuthProvider's onAuthStateChange
// stays the source of truth. Every switch clears the React Query cache so no
// user-scoped data leaks across accounts.

export interface AccountSwitcherApi {
  /** Real, switchable accounts (never anonymous), most-recent snapshot. */
  accounts: StoredAccount[];
  /** The currently-active stored account id, if any. */
  activeUserId: string | null;
  /** Whether the "add another account" sign-in popup is open. */
  addAccountOpen: boolean;
  /** Open the sign-in popup to add a NEW account (does not convert anon). */
  beginAddAccount: () => void;
  /** Close the add-account popup. */
  setAddAccountOpen: (open: boolean) => void;
  /**
   * Make a stored account the active session. Resolves to `true` on success.
   * On an expired/invalid refresh token it returns `false` and opens the
   * add-account popup so the user can re-authenticate just that account.
   */
  switchAccount: (userId: string) => Promise<boolean>;
  /** Sign out of ONE account; switch to another stored account if available. */
  signOutThisAccount: (userId: string) => Promise<void>;
  /** Sign out of EVERY account and clear the store. */
  signOutAll: () => Promise<void>;
}

/**
 * Owns the multi-account store + switching side-effects. Instantiated once in
 * AuthProvider and exposed through AuthContext, so the Sidebar and AppMenuBar
 * share one source of truth.
 *
 * @param queryClient the app QueryClient — cleared on every switch for isolation
 * @param baseSignOut the AuthProvider's `supabase.auth.signOut` wrapper
 */
export function useAccountSwitcher(
  queryClient: QueryClient,
  baseSignOut: () => Promise<void>,
): AccountSwitcherApi {
  const [accounts, setAccounts] = useState<StoredAccount[]>(() =>
    listStoredAccounts(),
  );
  const [activeUserId, setActive] = useState<string | null>(() =>
    getActiveUserId(),
  );
  const [addAccountOpen, setAddAccountOpen] = useState(false);

  // Re-read the store on any localStorage change — covers writes made by the
  // provider's auth listener (which records accounts) and other tabs.
  const refresh = useCallback(() => {
    setAccounts(listStoredAccounts());
    setActive(getActiveUserId());
  }, []);

  useEffect(() => {
    window.addEventListener('storage', refresh);
    // Poll-free in-tab sync: the provider dispatches this after recording.
    window.addEventListener('tk-accounts-changed', refresh);
    refresh();
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('tk-accounts-changed', refresh);
    };
  }, [refresh]);

  const beginAddAccount = useCallback(() => setAddAccountOpen(true), []);

  const switchAccount = useCallback(
    async (userId: string): Promise<boolean> => {
      const target = listStoredAccounts().find((a) => a.userId === userId);
      if (!target) return false;
      // Already active — nothing to do.
      if (getActiveUserId() === userId) return true;

      const { data, error } = await supabase.auth.setSession({
        access_token: target.accessToken,
        refresh_token: target.refreshToken,
      });

      // An expired/invalid refresh token can't be revived silently — drop it to
      // re-login and open the add-account popup scoped to just this account.
      if (error || !data.session) {
        console.warn('[accounts] switch failed, re-login required', error);
        setAddAccountOpen(true);
        return false;
      }

      // `setSession` fires onAuthStateChange (provider records + activates the
      // account); the rotated tokens are persisted there. Clear cross-account
      // cached data so the new account never sees the previous one's queries.
      queryClient.clear();
      refresh();
      return true;
    },
    [queryClient, refresh],
  );

  const signOutThisAccount = useCallback(
    async (userId: string): Promise<void> => {
      // Capture which account is active BEFORE removing it (removeStoredAccount
      // clears activeUserId if it pointed here).
      const wasActive = getActiveUserId() === userId;
      const remaining = removeStoredAccount(userId);

      if (!wasActive) {
        // Dropping an inactive slot: just refresh the list, leave the session.
        refresh();
        return;
      }

      // The active account was removed. Switch to another stored account if one
      // exists; otherwise fully sign out (back to an anonymous session via the
      // provider's init flow).
      const next = remaining[0];
      if (next) {
        const { data, error } = await supabase.auth.setSession({
          access_token: next.accessToken,
          refresh_token: next.refreshToken,
        });
        if (error || !data.session) {
          // Next account's token is also dead — sign out cleanly instead.
          await baseSignOut();
        }
      } else {
        await baseSignOut();
      }
      queryClient.clear();
      refresh();
    },
    [baseSignOut, queryClient, refresh],
  );

  const signOutAll = useCallback(async (): Promise<void> => {
    clearStoredAccounts();
    await baseSignOut();
    queryClient.clear();
    refresh();
  }, [baseSignOut, queryClient, refresh]);

  return {
    accounts,
    activeUserId,
    addAccountOpen,
    beginAddAccount,
    setAddAccountOpen,
    switchAccount,
    signOutThisAccount,
    signOutAll,
  };
}
